<script lang="ts">
  /*
    Touch joystick for overworld movement (phones only — see device.svelte.ts).
    Emits a normalized [-1,1] direction vector on every pointer move and (0,0)
    on release; World.svelte feeds it into WorldRenderer.setStick(), which
    joins the existing keyboard/click-to-walk movement resolution.
    Left-thumb zone, matching every mobile MMO; tap-to-walk still works on the
    map itself, this is the "hold to steer" comfort layer.
  */
  let { onmove }: { onmove: (x: number, y: number) => void } = $props();

  const RADIUS = 44; // knob travel in px
  const DEADZONE = 0.22;

  let base = $state<HTMLDivElement | null>(null);
  let active = $state(false);
  let kx = $state(0); // knob offset, px
  let ky = $state(0);

  function vec(e: PointerEvent): { x: number; y: number } {
    const r = base!.getBoundingClientRect();
    const dx = e.clientX - (r.left + r.width / 2);
    const dy = e.clientY - (r.top + r.height / 2);
    const len = Math.hypot(dx, dy);
    const c = len > RADIUS ? RADIUS / len : 1;
    return { x: dx * c, y: dy * c };
  }

  function emit() {
    const x = kx / RADIUS;
    const y = ky / RADIUS;
    const dead = Math.hypot(x, y) < DEADZONE;
    onmove(dead ? 0 : x, dead ? 0 : y);
  }

  function down(e: PointerEvent) {
    try {
      base?.setPointerCapture(e.pointerId);
    } catch {
      /* pointer already gone (or synthetic) — move/up listeners still work */
    }
    active = true;
    ({ x: kx, y: ky } = vec(e));
    emit();
  }
  function move(e: PointerEvent) {
    if (!active) return;
    ({ x: kx, y: ky } = vec(e));
    emit();
  }
  function up() {
    active = false;
    kx = 0;
    ky = 0;
    onmove(0, 0);
  }
</script>

<div
  bind:this={base}
  class="pointer-events-auto fixed z-30 grid size-32 touch-none select-none place-items-center rounded-full border transition-colors duration-150 {active ? 'border-cyan/60 bg-void/45' : 'border-cyan-dim/40 bg-void/30'}"
  style="left:max(1.25rem, env(safe-area-inset-left)); bottom:max(1.25rem, env(safe-area-inset-bottom))"
  onpointerdown={down}
  onpointermove={move}
  onpointerup={up}
  onpointercancel={up}
  aria-hidden="true"
  data-joystick
>
  <!-- cross ticks: orient the idle stick like a compass, VRAINS-thin -->
  <div class="pointer-events-none absolute inset-3 rounded-full border border-cyan-dim/20"></div>
  <div
    class="pointer-events-none grid size-14 place-items-center rounded-full border bg-surface-2/80 {active ? 'border-cyan/80 glow-cyan' : 'border-cyan-dim/50'} {active ? '' : 'knob-rest'}"
    style="transform:translate({kx}px,{ky}px)"
  >
    <div class="size-2 rounded-full {active ? 'bg-cyan' : 'bg-cyan-dim/70'}"></div>
  </div>
</div>

<style>
  /* Snap home on release; live drag must NOT ease (it would lag the thumb). */
  .knob-rest {
    transition: transform 0.15s var(--ease-out-quint);
  }
</style>
